import { InfoCircledIcon } from "@radix-ui/react-icons";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import {
  DefaultPaymentPlan,
  FormPaymentPlan,
  FormsService,
} from "client/openapi";
import {
  COMPACT_INLINE_NUMBER,
  CompactRadio,
  FieldError,
} from "components/Inputs/compact";
import {
  SECTION_TEXT,
  SECTION_TITLE,
  wholeNumberError,
} from "components/ProfileDropdown/settingsStyles";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/Tooltip";
import { useCallback, useEffect, useState } from "react";
import { openOrganizationSettings } from "util/organizationSettings";

type Plan = Required<DefaultPaymentPlan>;

/** Andrew's names for the two ways of billing (pre_billing_enabled). */
const MODES = [
  { value: "post", label: "Post Meeting Payment" },
  { value: "pre", label: "Pre Meeting Payment" },
];

/** The limits the student's Automatic Billing dialog uses too. */
const MAX_INTERVAL_DAYS = 90;
const MAX_LOOKAHEAD_WEEKS = 12;

/** A default plan saved in Organization Settings, for the builder to show. */
const SAVED_EVENT = "tadpole:default-payment-plan-saved";
type SavedPlan = { orgId: number; plan: Plan };

/** Fill in what the server leaves to its defaults. */
function complete(plan: DefaultPaymentPlan): Plan {
  return {
    enabled: !!plan.enabled,
    interval: plan.interval ?? 1,
    pre_billing_enabled: !!plan.pre_billing_enabled,
    pre_billing_lookahead_weeks: plan.pre_billing_lookahead_weeks ?? 2,
  };
}

/** An allowed whole number as typed, or `fallback` while it isn't one. */
const typed = (text: string, max: number, fallback: number) =>
  wholeNumberError(text, 1, max) ? fallback : Number(text);

/** "1 day", "7 days". */
const count = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;

/** A plan that is on, named in PlanSchedule's words. */
function planName(plan: Plan) {
  return plan.pre_billing_enabled
    ? `Pre Meeting Payment, every ${count(
        plan.interval,
        "day"
      )} for sessions in the next ${count(
        plan.pre_billing_lookahead_weeks,
        "week"
      )}`
    : `Post Meeting Payment, every ${count(plan.interval, "day")}`;
}

/**
 * A plan's two numbers as typed: the plan's when it first has one, and what
 * load() fills in. Each stays text while it is typed; the errors count only
 * while the plan is on.
 */
function usePlanNumbers(plan: Plan | undefined) {
  const [interval, setIntervalText] = useState(
    plan ? String(plan.interval) : ""
  );
  const [weeks, setWeeksText] = useState(
    plan ? String(plan.pre_billing_lookahead_weeks) : ""
  );

  const intervalError =
    plan?.enabled && wholeNumberError(interval, 1, MAX_INTERVAL_DAYS);
  const weeksError =
    plan?.enabled &&
    plan.pre_billing_enabled &&
    wholeNumberError(weeks, 1, MAX_LOOKAHEAD_WEEKS);

  const load = useCallback((loaded: Plan) => {
    setIntervalText(String(loaded.interval));
    setWeeksText(String(loaded.pre_billing_lookahead_weeks));
  }, []);

  return {
    interval,
    weeks,
    intervalError,
    weeksError,
    invalid: !!intervalError || !!weeksError,
    setIntervalText,
    setWeeksText,
    load,
  };
}

type PlanNumbers = ReturnType<typeof usePlanNumbers>;

/**
 * The organization's default payment plan, loaded while `wanted`, for
 * Organization Settings. Choices change the plan at once; each number stays
 * text while it is typed and goes into the plan when you leave the box, if
 * it is allowed. The dialog's Save sends it with save(): `changed` says
 * there is something to send, numbers as typed, and reset() starts over, as
 * closing the dialog does.
 */
export function useDefaultPaymentPlan(
  orgId: number | null | undefined,
  { wanted = true } = {}
) {
  const [saved, setSaved] = useState<Plan>();
  const [plan, setPlan] = useState<Plan>();
  const [loadFailed, setLoadFailed] = useState(false);
  const numbers = usePlanNumbers(plan);
  const { load } = numbers;

  useEffect(() => {
    if (!orgId || !wanted) return;
    let current = true;
    FormsService.getDefaultPaymentPlan({ orgId })
      .then((loaded) => {
        if (!current) return;
        const next = complete(loaded);
        setSaved(next);
        setPlan(next);
        load(next);
      })
      .catch(() => current && setLoadFailed(true));
    return () => {
      current = false;
    };
  }, [orgId, wanted, load]);

  // What Save sends.
  const draft = plan && {
    ...plan,
    interval: typed(numbers.interval, MAX_INTERVAL_DAYS, plan.interval),
    pre_billing_lookahead_weeks: typed(
      numbers.weeks,
      MAX_LOOKAHEAD_WEEKS,
      plan.pre_billing_lookahead_weeks
    ),
  };

  return {
    ...numbers,
    plan,
    loadFailed,
    set: (changes: Partial<Plan>) =>
      setPlan((current) => current && { ...current, ...changes }),
    // A number not allowed yet is an edit too, as on the other tabs.
    changed:
      numbers.invalid ||
      (!!draft &&
        !!saved &&
        (Object.keys(saved) as (keyof Plan)[]).some(
          (key) => draft[key] !== saved[key]
        )),
    save: async () => {
      if (!draft) return;
      await FormsService.updateDefaultPaymentPlan({
        orgId: orgId as number,
        requestBody: draft,
      });
      setSaved(draft);
      // The form builder shows the default its forms fall back to.
      window.dispatchEvent(
        new CustomEvent<SavedPlan>(SAVED_EVENT, {
          detail: { orgId: orgId as number, plan: draft },
        })
      );
    },
    reset: () => {
      setSaved(undefined);
      setPlan(undefined);
      setLoadFailed(false);
    },
  };
}

export type DefaultPaymentPlanForm = ReturnType<typeof useDefaultPaymentPlan>;

/**
 * How a plan that is on bills: Post or Pre Meeting Payment, then one
 * sentence with its numbers in it. `idPrefix` keeps the boxes' ids apart
 * where two of these can be on the page (the builder and the Organization
 * Settings dialog).
 */
function PlanSchedule({
  plan,
  numbers,
  set,
  idPrefix,
  className = "",
}: {
  plan: Plan;
  numbers: PlanNumbers;
  set: (changes: Partial<Plan>) => void;
  idPrefix: string;
  className?: string;
}) {
  const {
    interval,
    weeks,
    intervalError,
    weeksError,
    setIntervalText,
    setWeeksText,
  } = numbers;
  return (
    <div className={`flex flex-col items-start gap-2 ${className}`}>
      {/* When families pay; each mode shows only its own settings. */}
      <ToggleGroup.Root
        type="single"
        aria-label="When families pay"
        className="inline-flex rounded-full bg-gray-100 p-0.5"
        value={plan.pre_billing_enabled ? "pre" : "post"}
        onValueChange={(mode) =>
          mode && set({ pre_billing_enabled: mode === "pre" })
        }
      >
        {MODES.map(({ value, label }) => (
          <ToggleGroup.Item
            key={value}
            value={value}
            className="rounded-full px-3 py-1 text-center text-xs font-semibold leading-4 text-gray-600 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 data-[state=on]:bg-white data-[state=on]:text-green-700 data-[state=on]:shadow-sm"
          >
            {label}
          </ToggleGroup.Item>
        ))}
      </ToggleGroup.Root>

      {/* One sentence that wraps like text, the boxes inline. The
          interval is shared, so switching keeps it. */}
      <p className="leading-6">
        <label htmlFor={`${idPrefix}-interval`}>Every</label>
        <input
          id={`${idPrefix}-interval`}
          type="number"
          inputMode="numeric"
          min={1}
          max={MAX_INTERVAL_DAYS}
          step={1}
          className={COMPACT_INLINE_NUMBER}
          aria-invalid={!!intervalError}
          aria-describedby={
            intervalError ? `${idPrefix}-interval-error` : undefined
          }
          value={interval}
          onChange={(e) => setIntervalText(e.target.value)}
          onBlur={() => {
            if (!intervalError) set({ interval: Number(interval) });
          }}
        />
        {interval.trim() === "1" ? "day" : "days"},{" "}
        {plan.pre_billing_enabled ? (
          <>
            <label htmlFor={`${idPrefix}-weeks`}>
              charge families upfront for sessions booked in the next
            </label>
            <input
              id={`${idPrefix}-weeks`}
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_LOOKAHEAD_WEEKS}
              step={1}
              className={COMPACT_INLINE_NUMBER}
              aria-invalid={!!weeksError}
              aria-describedby={
                weeksError ? `${idPrefix}-weeks-error` : undefined
              }
              value={weeks}
              onChange={(e) => setWeeksText(e.target.value)}
              onBlur={() => {
                if (!weeksError) {
                  set({ pre_billing_lookahead_weeks: Number(weeks) });
                }
              }}
            />
            {weeks.trim() === "1" ? "week" : "weeks"}, minus any credit they
            already have.
            <Tooltip>
              <TooltipTrigger>
                <button
                  type="button"
                  aria-label="About paying before meetings"
                  className="ml-1 align-middle text-gray-500 hover:text-gray-700"
                >
                  <InfoCircledIcon className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <div className="w-64">
                  Each billing run charges for upcoming sessions in the window,
                  minus any credit the family already has. When a session is
                  approved and billed, it&apos;s paid from that credit, so
                  nothing is charged twice. Anything the credit doesn&apos;t
                  cover is charged at the next run.
                </div>
              </TooltipContent>
            </Tooltip>
          </>
        ) : (
          <>
            charge families for sessions that have happened and been approved
            but not billed yet. Credit on file is used first.
          </>
        )}
      </p>
      {intervalError && (
        <FieldError id={`${idPrefix}-interval-error`}>
          {intervalError}
        </FieldError>
      )}
      {weeksError && (
        <FieldError id={`${idPrefix}-weeks-error`}>{weeksError}</FieldError>
      )}
    </div>
  );
}

/**
 * The organization's default payment plan, in Organization Settings. When
 * a family saves a payment method in a form that doesn't use its own plan,
 * or through a payment method request sent by text or email, each of their
 * students with no plan is put on it. Its bottom margin pairs with the
 * card's small bottom padding.
 */
export function DefaultPaymentPlanFields({
  paymentPlan,
}: {
  paymentPlan: DefaultPaymentPlanForm;
}) {
  const { plan, loadFailed, set } = paymentPlan;

  if (loadFailed) {
    return (
      <p className="mb-4 text-sm text-red-600">
        The default payment plan could not be loaded.
      </p>
    );
  }
  if (!plan) return null;

  return (
    <div className="mb-4 flex flex-col text-sm">
      <h3 className={SECTION_TITLE}>Default payment plan</h3>
      <p className={SECTION_TEXT}>
        Students whose automatic billing is off join it when a payment method is
        saved for their family. That happens in a form, from a text or email
        request, when staff add one, or when the family adds one in their own
        Tadpole account. A form can use its own plan instead.
      </p>
      <div className="mt-3 flex gap-5">
        <CompactRadio
          id="default-plan-on"
          name="default-plan"
          checked={plan.enabled}
          onChange={() => set({ enabled: true })}
        >
          On
        </CompactRadio>
        <CompactRadio
          id="default-plan-off"
          name="default-plan"
          checked={!plan.enabled}
          onChange={() => set({ enabled: false })}
        >
          Off
        </CompactRadio>
      </div>
      {plan.enabled && (
        <PlanSchedule
          plan={plan}
          numbers={paymentPlan}
          set={set}
          idPrefix="default-plan"
          className="mt-3 border-t border-gray-100 pt-3"
        />
      )}
    </div>
  );
}

/**
 * A form's payment plan, in the form builder: the organization's default
 * plan, shown as it is now, or a plan for this form. It is part of the
 * form's definition, so it saves with the form. Each number goes into the
 * plan when you leave its box, if it is allowed; onInvalid says while one
 * is not, so the builder can say it is not saved.
 */
export function FormPaymentPlanFields({
  orgId,
  plan,
  onChange,
  onInvalid,
}: {
  orgId: number;
  plan?: FormPaymentPlan | null;
  onChange: (plan: FormPaymentPlan) => void;
  onInvalid: (invalid: boolean) => void;
}) {
  const [orgPlan, setOrgPlan] = useState<Plan>();
  const [loadFailed, setLoadFailed] = useState(false);
  // Forms saved before this have no plan: they use the default.
  const own = plan ? complete(plan) : undefined;
  const numbers = usePlanNumbers(own);
  const { invalid } = numbers;

  // The default as saved, and again each time Organization Settings saves it.
  useEffect(() => {
    let current = true;
    FormsService.getDefaultPaymentPlan({ orgId })
      .then((loaded) => current && setOrgPlan(complete(loaded)))
      .catch(() => current && setLoadFailed(true));
    const listener = (event: Event) => {
      const saved = (event as CustomEvent<SavedPlan>).detail;
      if (saved.orgId !== orgId) return;
      setOrgPlan(saved.plan);
      setLoadFailed(false);
    };
    window.addEventListener(SAVED_EVENT, listener);
    return () => {
      current = false;
      window.removeEventListener(SAVED_EVENT, listener);
    };
  }, [orgId]);

  // The builder says "Not saved" while a number isn't allowed; once this is
  // hidden (payment turned off), nothing waits on it.
  useEffect(() => {
    onInvalid(invalid);
    return () => onInvalid(false);
  }, [invalid, onInvalid]);

  if (!orgPlan && !loadFailed) return null;

  /**
   * The first time, the form's plan starts as the default's mode and
   * numbers, so changing one thing is that one change; after that, it finds
   * the form's own numbers again.
   */
  function chooseOwnPlan() {
    const start = own ?? complete(orgPlan ?? {});
    if (!own) numbers.load(start);
    onChange({ ...start, enabled: true });
  }

  // With no default yet, the link is to set one up.
  const noDefault = !loadFailed && !orgPlan?.enabled;

  return (
    <div className="mb-4 flex flex-col text-sm">
      <h3 className={SECTION_TITLE}>Payment plan</h3>
      <p className={SECTION_TEXT}>
        {loadFailed ? (
          <span className="text-red-600">
            The default payment plan could not be loaded.
          </span>
        ) : orgPlan?.enabled ? (
          <>
            Your organization&apos;s default plan is{" "}
            <span className="font-semibold text-gray-900">
              {planName(orgPlan)}
            </span>
            . When a family saves a card or bank on this form, each student
            without a plan is put on it.
          </>
        ) : (
          "Your organization has no default plan, so families who save a card here aren't put on a plan automatically."
        )}{" "}
        <button
          type="button"
          className="font-medium text-gray-700 underline"
          onClick={() => openOrganizationSettings("Billing")}
        >
          {noDefault
            ? "Set one up in Organization Settings."
            : "Change the default in Organization Settings."}
        </button>
      </p>
      <div className="mt-3 flex flex-col gap-3">
        <div>
          <CompactRadio
            id="form-plan-default"
            name="form-plan"
            checked={!own?.enabled}
            onChange={() => own && onChange({ ...own, enabled: false })}
          >
            Use the default plan
          </CompactRadio>
        </div>
        <div>
          <CompactRadio
            id="form-plan-own"
            name="form-plan"
            checked={!!own?.enabled}
            onChange={chooseOwnPlan}
          >
            Use a different plan for this form
          </CompactRadio>
          <p className="mt-0.5 pl-[1.375rem] text-gray-600">
            The students on this form move to this plan when the family submits
            it, even if they&apos;re already on a plan. Other forms and payment
            requests keep the default.
          </p>
          {own?.enabled && (
            <PlanSchedule
              plan={own}
              numbers={numbers}
              set={(changes) => onChange({ ...own, ...changes })}
              idPrefix="form-plan"
              className="mt-2 pl-[1.375rem]"
            />
          )}
        </div>
      </div>
    </div>
  );
}
